/**
 * MyDeals.jsx — Buyer or bidder lists deals split into Active and Completed sections.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';
import { formatDealsLoadError } from '../../utils/dealsApiErrors';
import { viewerHasRated } from '../../utils/ratingHint';

const STATUS_COLORS = {
  ACTIVE:      'info',
  IN_PROGRESS: 'warning',
  COMPLETED:   'success',
  CLOSED:      'default',
};

const ACTIVE_STATUSES    = ['ACTIVE', 'IN_PROGRESS'];
const COMPLETED_STATUSES = ['COMPLETED', 'CLOSED'];

function DealCard({ d, navigate }) {
  const isCompleted = COMPLETED_STATUSES.includes(d.deal_status);
  const canRate     = d.deal_status === 'CLOSED' && !viewerHasRated(d, d.id);

  return (
    <Paper key={d.id} sx={{ p: 2, mb: 2 }} elevation={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6">Deal #{d.id}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Value: <strong>Rs.{Number(d.deal_value).toLocaleString('en-IN')}</strong>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          <Chip
            label={d.deal_status.replace('_', ' ')}
            size="small"
            color={STATUS_COLORS[d.deal_status] || 'default'}
          />
          {d.escrow_available && (
            <Chip label="Escrow" size="small" color="success" variant="outlined" />
          )}
          {isCompleted && d.anonymity_release_flag && (
            <Chip label="Identity revealed" size="small" color="success" variant="outlined" />
          )}
          {d.deal_status === 'CLOSED' && viewerHasRated(d, d.id) && (
            <Chip label="Rated ★" size="small" color="secondary" variant="outlined" />
          )}
        </Box>
      </Box>

      {/* Disputes */}
      {Array.isArray(d.disputes) && d.disputes.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Disputes:</Typography>
          {d.disputes.map((disp) => (
            <Chip
              key={disp.id}
              label={`#${disp.id} ${disp.dispute_status}`}
              size="small"
              color={disp.dispute_status === 'OPEN' ? 'warning' : 'default'}
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {/* Completed deal summary */}
      {isCompleted && (
        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Deal closed {d.closed_at ? `on ${new Date(d.closed_at).toLocaleDateString()}` : ''}
          </Typography>
          {d.deal_status === 'CLOSED' && !viewerHasRated(d, d.id) && (
            <Alert severity="success" sx={{ mt: 1, py: 0.5 }}>
              Rating window is open — rate the other party to complete this deal.
            </Alert>
          )}
          {d.deal_status === 'CLOSED' && viewerHasRated(d, d.id) && (
            <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
              You have submitted your rating for this deal.
            </Alert>
          )}
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/deals/${d.id}`)}
          aria-label={`Open deal ${d.id}`}
        >
          Open deal
        </Button>
        {canRate && (
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<StarOutlineIcon />}
            onClick={() => navigate(`/ratings/submit/${d.id}`)}
            aria-label={`Submit rating for deal ${d.id}`}
          >
            Rate deal
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export default function MyDeals() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [deals, setDeals]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [tab, setTab]       = useState(0); // 0 = active, 1 = completed

  useEffect(() => {
    const role = localStorage.getItem('cmp_role');
    if (role === 'ARBITRATOR') {
      navigate('/arbitrations/mine');
      return;
    } else if (role === 'ADMIN') {
      navigate('/admin/disputes');
      return;
    }

    setLoading(true);
    setError('');
    apiClient
      .get('/api/v1/deals/mine/')
      .then(({ data }) => setDeals(Array.isArray(data) ? data : []))
      .catch((err) => setError(formatDealsLoadError(err)))
      .finally(() => setLoading(false));
  }, [location.key, navigate]);

  const activeDeals    = useMemo(() => deals.filter((d) => ACTIVE_STATUSES.includes(d.deal_status)), [deals]);
  const completedDeals = useMemo(() => deals.filter((d) => COMPLETED_STATUSES.includes(d.deal_status)), [deals]);
  const pendingRating  = completedDeals.filter((d) => d.deal_status === 'CLOSED' && !viewerHasRated(d, d.id)).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress aria-label="Loading deals" />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{
          mb: 3,
          textTransform: 'none',
          borderColor: 'rgba(255,255,255,0.12)',
          color: '#cbd5e1',
          '&:hover': {
            borderColor: '#818cf8',
            bgcolor: 'rgba(129, 140, 248, 0.04)',
          }
        }}
      >
        Back to Home
      </Button>
      <Typography variant="h4" gutterBottom>My Deals</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {deals.length === 0 ? (
        <Typography color="text.secondary">No deals yet.</Typography>
      ) : (
        <>
          {/* Tab bar */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<HandshakeOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Active
                  {activeDeals.length > 0 && (
                    <Chip label={activeDeals.length} size="small" color="info" sx={{ height: 18, fontSize: '0.7rem' }} />
                  )}
                </Box>
              }
              aria-label="Active deals"
            />
            <Tab
              icon={<CheckCircleOutlineIcon fontSize="small" />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Completed
                  {completedDeals.length > 0 && (
                    <Chip label={completedDeals.length} size="small" color="default" sx={{ height: 18, fontSize: '0.7rem' }} />
                  )}
                  {pendingRating > 0 && (
                    <Chip label={`${pendingRating} to rate`} size="small" color="secondary" sx={{ height: 18, fontSize: '0.7rem' }} />
                  )}
                </Box>
              }
              aria-label="Completed deals"
            />
          </Tabs>

          {/* Active deals tab */}
          {tab === 0 && (
            <>
              {activeDeals.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <HandshakeOutlinedIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography>No active deals right now.</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Active deals appear here once a bid is selected and work begins.
                  </Typography>
                </Box>
              ) : (
                activeDeals.map((d) => <DealCard key={d.id} d={d} navigate={navigate} />)
              )}
            </>
          )}

          {/* Completed deals tab */}
          {tab === 1 && (
            <>
              {pendingRating > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You have <strong>{pendingRating}</strong> deal{pendingRating > 1 ? 's' : ''} waiting for your rating.
                  Rate the other party to complete the feedback loop.
                </Alert>
              )}
              {completedDeals.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography>No completed deals yet.</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Deals appear here once they are closed by an administrator.
                  </Typography>
                </Box>
              ) : (
                completedDeals.map((d) => <DealCard key={d.id} d={d} navigate={navigate} />)
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
